import {
  type AccountDeletionResult,
  ApiResponseError,
  createApiClient,
  createLubeHubApi,
  type User,
} from '@lubehub/api';
import { Button, Card, CardBody, Field, Stack, Text, TextField } from '@lubehub/ui-web';
import { type SyntheticEvent, useMemo, useState } from 'react';

import { loginFromIdentifier } from '../accountDeletion/loginIdentifier';
import { apiBaseUrl } from '../config';
import { t } from '../copy';
import { SUPPORT_EMAIL } from '../legal/identity';
import { usePublicLocale } from '../locale';

type DeletionApi = ReturnType<typeof createLubeHubApi>;

export interface AccountDeletionPageProps {
  /** Tests inject a stub so no network call is made. */
  apiFactory?: (getToken: () => string | null) => DeletionApi;
}

function defaultApiFactory(getToken: () => string | null): DeletionApi {
  return createLubeHubApi(
    createApiClient({
      baseUrl: apiBaseUrl(),
      getToken,
    }),
  );
}

function describeAuthError(error: unknown, locale: 'ar' | 'en'): string {
  // No answer at all (offline, blocked) is not a wrong password: saying it is
  // sends people off retrying credentials that were right.
  if (!(error instanceof ApiResponseError)) {
    return t(locale, 'deletion.error.network');
  }
  if (error.status === 429) {
    return t(locale, 'deletion.error.rate');
  }
  if (error.status >= 500) {
    return t(locale, 'deletion.error.server');
  }

  return t(locale, 'deletion.error.generic');
}

export function AccountDeletionPage({ apiFactory = defaultApiFactory }: AccountDeletionPageProps) {
  const { locale } = usePublicLocale();
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [reauthPassword, setReauthPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AccountDeletionResult | null>(null);

  const api = useMemo(() => apiFactory(() => token), [apiFactory, token]);
  const phrase = t(locale, 'deletion.confirm.phrase');

  const signIn = (event: SyntheticEvent) => {
    event.preventDefault();
    setAuthError(null);
    setSubmitting(true);

    const credential = loginFromIdentifier(identifier, password);

    api.auth
      .login({ ...credential, device_name: 'carcarna-account-deletion' })
      .then((session) => {
        setToken(session.token);
        setUser(session.user);
        setPassword('');
      })
      .catch((error: unknown) => {
        setAuthError(describeAuthError(error, locale));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const signOut = () => {
    const currentApi = api;
    setToken(null);
    setUser(null);
    setConfirmation('');
    setReauthPassword('');
    setDeleteError(null);
    void currentApi.auth.logout().catch(() => undefined);
  };

  const submitDeletion = (event: SyntheticEvent) => {
    event.preventDefault();
    setDeleteError(null);

    if (confirmation.trim() !== phrase) {
      setDeleteError(t(locale, 'deletion.error.confirm'));
      return;
    }

    if (reauthPassword.trim() === '') {
      // An empty field is not a failed sign-in; it said "could not sign in".
      setDeleteError(t(locale, 'deletion.error.passwordRequired'));
      return;
    }

    setSubmitting(true);

    api.auth
      .deleteAccount({ password: reauthPassword })
      .then((next) => {
        setResult(next);
        setToken(null);
        setReauthPassword('');
      })
      .catch((error: unknown) => {
        if (error instanceof ApiResponseError && error.status === 422) {
          setDeleteError(t(locale, 'deletion.error.password'));
          return;
        }
        if (error instanceof ApiResponseError && error.status === 409) {
          setDeleteError(t(locale, 'deletion.error.blocked'));
          return;
        }
        // A server failure during deletion is not a sign-in failure either.
        if (error instanceof ApiResponseError && error.status >= 500) {
          setDeleteError(t(locale, 'deletion.error.server'));
          return;
        }
        setDeleteError(describeAuthError(error, locale));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (result) {
    return (
      <Stack gap="lg">
        <Text as="h1" variant="display">
          {t(locale, 'deletion.done.title')}
        </Text>
        <Card>
          <CardBody>
            <Stack gap="sm">
              <Text variant="bodySm" tone="secondary">
                {t(locale, 'deletion.done.reference')}
              </Text>
              <Text variant="title">
                <span data-testid="deletion-reference">{result.deletion_reference}</span>
              </Text>
              <Text variant="body">
                {result.email_confirmation_sent
                  ? t(locale, 'deletion.done.email')
                  : t(locale, 'deletion.done.noEmail')}
              </Text>
            </Stack>
          </CardBody>
        </Card>
      </Stack>
    );
  }

  if (!user || !token) {
    return (
      <Stack gap="lg">
        <Text as="h1" variant="display">
          {t(locale, 'deletion.title')}
        </Text>
        <Text variant="body">{t(locale, 'deletion.lead')}</Text>
        <Card>
          <CardBody>
            <form onSubmit={signIn} data-testid="deletion-login">
              <Stack gap="md">
                {authError ? (
                  <Text variant="bodySm" tone="danger" role="alert">
                    {authError}
                  </Text>
                ) : null}
                <Field label={t(locale, 'deletion.identifier')}>
                  {(props) => (
                    <TextField
                      {...props}
                      name="identifier"
                      autoComplete="username"
                      value={identifier}
                      onChange={(event) => {
                        setIdentifier(event.target.value);
                      }}
                      required
                      data-testid="deletion-identifier"
                    />
                  )}
                </Field>
                <Field label={t(locale, 'deletion.password')}>
                  {(props) => (
                    <TextField
                      {...props}
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                      }}
                      required
                      data-testid="deletion-password"
                    />
                  )}
                </Field>
                <Button type="submit" loading={submitting} data-testid="deletion-sign-in">
                  {t(locale, 'deletion.signIn')}
                </Button>
              </Stack>
            </form>
          </CardBody>
        </Card>
        <Text variant="bodySm" tone="secondary">
          {t(locale, 'deletion.help')}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Text as="h1" variant="display">
        {t(locale, 'deletion.title')}
      </Text>
      <Text variant="body">
        <span data-testid="deletion-identity">
          {t(locale, 'deletion.signedInAs')} {user.name}
          {user.phone ? ` · ${user.phone}` : ''}
          {user.email ? ` · ${user.email}` : ''}
        </span>
      </Text>
      <Button variant="ghost" type="button" onClick={signOut} data-testid="deletion-sign-out">
        {t(locale, 'deletion.signOut')}
      </Button>
      <Card>
        <CardBody>
          <Stack gap="sm">
            <Text variant="title">{t(locale, 'deletion.meaning.title')}</Text>
            <Text variant="body">{t(locale, 'deletion.meaning.access')}</Text>
            <Text variant="body">{t(locale, 'deletion.meaning.personal')}</Text>
            <Text variant="body">{t(locale, 'deletion.meaning.retain')}</Text>
            <Text variant="body">{t(locale, 'deletion.meaning.blockers')}</Text>
          </Stack>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <form onSubmit={submitDeletion} data-testid="deletion-confirm">
            <Stack gap="md">
              {deleteError ? (
                <Text variant="bodySm" tone="danger" role="alert">
                  {deleteError}
                </Text>
              ) : null}
              <Field label={t(locale, 'deletion.confirm.label')}>
                {(props) => (
                  <TextField
                    {...props}
                    name="confirmation"
                    autoComplete="off"
                    value={confirmation}
                    onChange={(event) => {
                      setConfirmation(event.target.value);
                    }}
                    data-testid="deletion-phrase"
                  />
                )}
              </Field>
              <Field label={t(locale, 'deletion.password')}>
                {(props) => (
                  <TextField
                    {...props}
                    name="current-password"
                    type="password"
                    autoComplete="current-password"
                    value={reauthPassword}
                    onChange={(event) => {
                      setReauthPassword(event.target.value);
                    }}
                    data-testid="deletion-reauth-password"
                  />
                )}
              </Field>
              <Button
                type="submit"
                variant="destructive"
                loading={submitting}
                data-testid="deletion-submit"
              >
                {t(locale, 'deletion.submit')}
              </Button>
            </Stack>
          </form>
        </CardBody>
      </Card>
      <Text variant="bodySm" tone="secondary">
        {SUPPORT_EMAIL}
      </Text>
    </Stack>
  );
}
